import type { FunctionalComponent as FC } from 'preact';
import { useCallback, useEffect, useState } from 'preact/hooks';

import { useVkCaptchaContext } from '../../context/VkCaptchaContext';
import { useWidget } from '../../context/WidgetContext';
import { useMessageJson } from '../../hooks/useMessageJson';
import type { CsatRatingMessage as Props, MessageJsonButtons } from '../../types/messages';
import { WebsocketEvents, WebsocketMessageContentType } from '../../types/ws-events';
import { useStompClient } from 'react-stomp-hooks';

import { RateAnswer } from '../RateAnswer';

import styles from './styles.module.css';

export const MessageCsatRating: FC<Props> = ({ messageJson, id }) => {
  const stompClient = useStompClient();

  const { setMessages } = useWidget();
  const { handleCaptchaOpen, isCaptchaPassed } = useVkCaptchaContext();

  const { messageJsonSelectedValue, csatSelectedValueIndex, messageButtons } = useMessageJson(messageJson);

  // Значения звезд в компоненте RateAnswer начинаются с 1 и до 5
  const [csatRating, setCsatRating] = useState(csatSelectedValueIndex ? csatSelectedValueIndex + 1 : 0);
  const [readonly, setReadonly] = useState(Boolean(messageJsonSelectedValue));
  const [pendingButtonIndex, setPendingButtonIndex] = useState<number | null>(null);

  // Отправка оценки в сокет и обновление состояния с сообщениями
  const handleSocketPublish = useCallback(
    (buttonIndex: number) => {
      if (stompClient) {
        stompClient.publish({
          destination: WebsocketEvents.AppMessage,
          body: JSON.stringify({
            messageContentType: WebsocketMessageContentType.JsonCsatResponse,
            messageJson: { value: messageButtons?.[buttonIndex].value },
            message: messageButtons?.[buttonIndex].text,
          }),
        });
      }

      setMessages?.((prevMessages) => {
        return prevMessages.map((message) =>
          message.id === id
            ? {
                ...message,
                messageJson: {
                  ...(message.messageJson as MessageJsonButtons), // точно знаем, что тут кнопки
                  selectedValue: messageButtons?.[buttonIndex].value,
                },
              }
            : message
        );
      });
    },
    [id, messageButtons, setMessages, stompClient]
  );

  const handleChangeRating = useCallback(
    (rate: number) => {
      setCsatRating(rate);
      setReadonly(true);

      // Нумерация в рейтинге начинается с 1, а индексы в кнопках - с 0
      const buttonIndex = rate - 1;

      if (isCaptchaPassed) {
        // Отправляем сразу, если капча пройдена
        handleSocketPublish(buttonIndex);
      } else {
        // Добавляем рейтинг в очередь, чтобы отправить сразу после прохождения капчи
        setPendingButtonIndex(buttonIndex);

        // Открываем капчу для прохождения
        handleCaptchaOpen();
      }
    },
    [handleCaptchaOpen, handleSocketPublish, isCaptchaPassed]
  );

  // Отслеживаем состояние прохождения капчи и наличия неотправленных оценок
  useEffect(() => {
    if (isCaptchaPassed && pendingButtonIndex !== null && stompClient) {
      handleSocketPublish(pendingButtonIndex);
      setPendingButtonIndex(null);
    }
  }, [handleSocketPublish, isCaptchaPassed, pendingButtonIndex, stompClient]);

  return (
    <RateAnswer
      className={styles.csatRating}
      rating={csatRating}
      onChangeRating={handleChangeRating}
      readonly={readonly}
    />
  );
};
